import {PropertyConfiguration} from "@app/core-app/app/domain/notion/db-properties";
import {
  defaultOutboxFieldNames, OutboxFieldNames,
  OutboxLegacyConfigReport,
  OutboxV2ConfigReport
} from "@app/core-app/app/domain/notion/configurations/outbox";
import {DatabaseInfo} from "@app/core-app/app/data/notion/DatabaseInfo";

export type OutboxProperties = {
    properties: any
}

export type OutboxPropertyConfiguration = PropertyConfiguration<OutboxFieldNames>;


export const getLegacyOutboxProperties = (report: OutboxLegacyConfigReport): OutboxPropertyConfiguration[] => {
  return [
    {
      property: report.status.ok ? report.status.name : undefined,
      selectedProperty: null,
      name: "Email status property:",
      description: "Please create \"Status\" or \"Email Status\" property. The property will be used to track email status"
    },
    {
      property: report.status.ok ? report.status.start : undefined,
      selectedProperty: null,

      name: "Start status property:",
      description: `Please create "Outbox" value for status property. The property value will be used to detect the emails that has to be sent`
    },
    {
      property: report.status.ok ? report.status.finish : undefined,
      selectedProperty: null,

      name: "Sent status property:",
      description: `Please create "Sent" value for status property. The property value will be set for sent emails`
    },
    {
      property: report.recipient.ok ? report.recipient.name : undefined,
      selectedProperty: null,

      name: "Recipient property:",
      description: `Please create "Recipient" property with "email" type. The property will be used as recipient list for the emails`
    },
    {
      property: report.sendAt.ok ? report.sendAt.name : undefined,
      selectedProperty: null,

      name: "Send At property:",
      description: `Please create "Send at" property with "date" type. The property will be used as sending time (if set)`
    },
    {
      property: report.attachments.ok ? report.attachments.name : undefined,
      selectedProperty: null,
      name: "Attachments property:",
      description: `Please create "Attachments" property with "files" type. The property will be used to store attachments`
    }
  ]
}


export const createLegacyOutboxProperties = (): OutboxProperties => {
  return {
    properties: {
      "Email Status": {
        name: "Email Status",
        type: "select",
        select: {
          options: [
            {name: "Outbox"},
            {name: "Sent"}
          ]
        }
      },
      "Recipient": {
        name: "Recipient",
        type: "email",
        email: {}
      },
      "Send at": {
        name: "Send at",
        type: "date",
        date: {}
      }
    }
  }
}


export const getV2OutboxProperties = (report: OutboxV2ConfigReport, databaseInfo: DatabaseInfo): OutboxPropertyConfiguration[] => {
  const customFields = databaseInfo.outboxFieldNames

  return [
    {
      property: report.subject?.ok ? report.subject?.name : undefined,
      selectedProperty: customFields.subject,
      modify: (fields, newVal) => ({...(fields || defaultOutboxFieldNames), subject: newVal}),
      name: "Subject property:",
      description: "The property will be used as email Subject. Required, must have title or text type. Please create \"Name\" title/text property."
    },
    {
      property: report.readyToSend.ok ? report.readyToSend.name : undefined,
      selectedProperty: customFields.readyToSend,
      modify: (fields, newVal) => ({...(fields || defaultOutboxFieldNames), readyToSend: newVal}),
      name: "Ready to send property:",
      description: "The property will be used to track email readiness for delivery. Required, must have checkbox type. Please create \"Ready to send\" checkbox property."
    },
    {
      property: report.sent.ok ? report.sent.name : undefined,
      selectedProperty: customFields.sent,
      modify: (fields, newVal) => ({...(fields || defaultOutboxFieldNames), sent: newVal}),
      name: "Sent property",
      description: `The property value will be used mark sent emails. Required, must have checkbox type. Please create "Sent" checkbox property. `
    },
    {
      property: report.recipient.ok ? report.recipient.name : undefined,
      selectedProperty: customFields.recipient,
      modify: (fields, newVal) => ({...(fields || defaultOutboxFieldNames), recipient: newVal}),
      name: "Recipient property:",
      description: `The property will be used as recipient list for the emails. Required. Please create "Recipient" property with "email" type. `
    },
    {
      property: report.sendAt.ok ? report.sendAt.name : undefined,
      selectedProperty: customFields.sendAt,
      modify: (fields, newVal) => ({...(fields || defaultOutboxFieldNames), sendAt: newVal}),

      name: "Send At property:",
      description: `The property will be used as sending time (if set). Required, must have date type. Please create "Send at" property with "date" type. `
    },
    {
      property: report.attachments.ok ? report.attachments.name : undefined,
      selectedProperty: customFields.attachments,
      modify: (fields, newVal) => ({...(fields || defaultOutboxFieldNames), attachments: newVal}),

      name: "Attachments property:",
      description: `The property will be used for attachments to send. Must have files type. Please create "Attachments" property with "files" type. `
    },
    {
      property: report.inReplyTo.ok ? report.inReplyTo.name : undefined,
      selectedProperty: customFields.inReplyTo,
      modify: (fields, newVal) => ({...(fields || defaultOutboxFieldNames), inReplyTo: newVal}),
      name: "In Reply To property:",
      optional: true,
      description: `Optional property will be used to track replies. Must have relation type. Please create "In Reply To" property with "relation" type and specify other outbox or inbox database. `
    },
    {
      property: report.error?.ok ? report.error?.name : undefined,
      selectedProperty: customFields.error,
      modify: (fields, newVal) => ({...(fields || defaultOutboxFieldNames), error: newVal}),
      name: "Error property:",
      optional: true,
      description: `Optional property will be used to indicate if there are any errors during processing. Must have checkbox type.`
    },

  ]
}
export const createV2OutboxProperties = (): OutboxProperties => {
  return {
    properties: {
      "Ready to send": {
        name: "Ready to send",
        type: "checkbox",
        checkbox: {}
      },
      "Sent": {
        name: "Sent",
        type: "checkbox",
        checkbox: {}
      },
      "Recipient": {
        name: "Recipient",
        type: "email",
        email: {}
      },
      "Send at": {
        name: "Send at",
        type: "date",
        date: {}
      },
      "Attachments": {
        name: "Attachments",
        type: "files",
        files: {}
      },
      "Error": {
        name: "Error",
        type: "checkbox",
        checkbox: {}
      },
    }
  }
}
